import React, { useEffect, useState, useRef } from "react";

import { T } from "services/lang/LanguageService";
import EasasGlobalConfirmDialog, {
  easasConfirmDialog,
} from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import useSound from "use-sound";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import alarm from "sounds/alarm-sound.mp3";
import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import DatasetStatusIndicator from "./DatasetStatusIndicator";
import {
  DatasetStatusMonitoringProps,
  StatusMonitoringIndicator,
  Visuals,
} from "../charts/interfaces";

const showConfirmationDialog = (
  setIsSoundConfirmed: (val: boolean) => void,
  setChildMute: (val: boolean) => void,
  setSoundOn: (val: boolean) => void,
  resumeAudioContext: () => void
) => {
  easasConfirmDialog({
    header: T("alert.attention"),
    message: T("alert.status-monitoring.in-this-tab-you-have-component-with-sound"),
    acceptText: T("word.ok"),
    cancelClassName: "hidden",
    confirmedCallback: () => {
      setIsSoundConfirmed(true);
      setChildMute(false);
      setSoundOn(true);
      resumeAudioContext();
    },
  });
};

const notExistingIndicator: StatusMonitoringIndicator = {
  number: 0,
  title: T("word.undefined"),
  icon: "pi pi-question",
  color: "#000000",
  visual: Visuals.NONE,
  sound: false,
};

enum AudioContextStates {
  RUNNING = "running",
  SUSPENDED = "suspended",
}

const DatasetStatusMonitoring = (props: DatasetStatusMonitoringProps) => {
  const audioContext = useRef<AudioContext>(new window.AudioContext());
  const identifier = props.identifiers?.[0] ?? "";
  const [isSoundConfirmed, setIsSoundConfirmed] = useState<boolean>(
    audioContext.current?.state === AudioContextStates.RUNNING
  );
  const [soundOn, setSoundOn] = useState<boolean>(
    audioContext.current?.state === AudioContextStates.RUNNING
  );
  const [childMute, setChildMute] = useState<boolean>(
    audioContext.current?.state === AudioContextStates.SUSPENDED
  );
  const [isSoundButtonEnabled, setIsSoundButtonEnabled] = useState<boolean>(false);
  const [currentIndicator, setCurrentIndicator] =
    useState<StatusMonitoringIndicator>(notExistingIndicator);

  const prevSound = useRef<boolean>(currentIndicator.sound);

  const confirmShown = useRef<boolean>(false);

  const [play, { stop }] = useSound(alarm, { interrupt: true, loop: true });

  const { data, error, isLoading, dataUpdatedAt } = useGetRecentData([identifier], {
    options: {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 10 * 1000,
    },
  });

  const [shouldTriggerAlarm, setShouldTriggerAlarm] = useState<boolean>(false);

  useEffect(() => {
    if (props.updateCheckTime) {
      const interval = setInterval(() => {
        const currentTime = new Date().getTime();
        const timeSinceLastUpdate = (currentTime - dataUpdatedAt) / 1000;

        if (timeSinceLastUpdate > props.updateCheckTime) {
          setShouldTriggerAlarm(true);
        } else {
          setShouldTriggerAlarm(false);
        }
      }, 1000);

      return () => clearInterval(interval);
    }

    return () => {};
  }, [dataUpdatedAt, props.updateCheckTime]);

  const checkAudioContext = () => {
    if (audioContext.current?.state === AudioContextStates.SUSPENDED && !confirmShown.current) {
      confirmShown.current = true;
      let isSoundSelected = false;
      props.indicators.forEach(indicator => {
        if (indicator.sound) {
          isSoundSelected = true;
        }
      });
      if (isSoundSelected) {
        setTimeout(() => {
          showConfirmationDialog(setIsSoundConfirmed, setChildMute, setSoundOn, resumeAudioContext);
        }, 2000);
      }
    }
  };
  const resumeAudioContext = () => {
    if (audioContext.current?.state === AudioContextStates.SUSPENDED) {
      audioContext.current
        .resume()
        .then(() => {
          if (soundOn && !childMute && currentIndicator.sound) {
            play();
          }
        })
        .catch(() => {
          easasAlert({
            summary: T("error.dataset-status-monitoring.error-resuming-audio-context"),
            severity: "error",
          });
        });
    }
  };

  useEffect(() => {
    if (!audioContext.current) {
      audioContext.current = new window.AudioContext();
    }
    checkAudioContext();
  }, [isSoundConfirmed]);

  useEffect(() => {
    let indicator = notExistingIndicator;

    if (data && data.length > 0 && data[0].data && data[0].data.length > 0) {
      const { value } = data[0].data[0];
      if (value || value === 0) {
        const foundIndicator = props.indicators.find(
          currIndicator => currIndicator.number === Number(value.toFixed(0))
        );
        if (foundIndicator) {
          indicator = foundIndicator;
        }
      }
    }
    setCurrentIndicator(indicator);

    if (indicator.sound) {
      setIsSoundButtonEnabled(true);
      if (soundOn && !childMute) {
        play();
      }
    } else {
      stop();
      setIsSoundButtonEnabled(false);
    }

    prevSound.current = indicator.sound;
  }, [data, childMute, soundOn, stop, play]);

  useEffect(() => {
    if (childMute || !currentIndicator.sound || !soundOn) {
      stop();
    }

    let intervalId: NodeJS.Timeout | undefined;

    if (soundOn && !childMute && currentIndicator.sound) {
      play();
      intervalId = setInterval(() => {
        if (soundOn && !childMute && currentIndicator.sound) {
          play();
        }
      }, 8000);
    } else {
      stop();
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [soundOn, childMute, play, stop, currentIndicator.sound]);

  if (isLoading) return <div>{T("word.loading")}</div>;
  if (error) return <div>{T("error.error-loading-data")}</div>;
  return (
    <div>
      <EasasGlobalConfirmDialog />
      {shouldTriggerAlarm && (
        <h4>
          {T("message.dataset-status-monitoring.last-update-was-more-than-seconds-ago", {
            updateCheckTime: props.updateCheckTime,
          })}
        </h4>
      )}
      <DatasetStatusIndicator
        currentIndicator={currentIndicator}
        scale={props.scale || 1}
        soundOn={soundOn}
        isMuted={childMute}
        setSoundOn={setSoundOn}
        setIsMuted={setChildMute}
        isSoundButtonEnabled={isSoundButtonEnabled}
      />
    </div>
  );
};

export default DatasetStatusMonitoring;
